import { GROUPED_TRANSFORM_RULES } from 'js/constants/transforms';
import { getCustomTxId, getCustomTxRule } from './TransformDSLUtils';

const stringify = (object: any): string => {
  const { type, name, parameters, __destAttr } = object;

  if (__destAttr) {
    const objectWithoutDestAttr = { ...object };
    delete objectWithoutDestAttr.__destAttr;

    return `${__destAttr} = ${stringify(objectWithoutDestAttr)}`;
  }

  if (type === 'function') {
    let rule: any;
    let args: string[];
    const customTxId = getCustomTxId(name);

    if (customTxId) {
      rule = getCustomTxRule(name);
      args = (parameters.attrTxId?.argValues || []).map(stringifyArg);
    } else {
      rule = GROUPED_TRANSFORM_RULES.rules[name];
      const requiredParamSpecsByName = rule.params?.reduce(
        (acc, param) => {
          acc[param.name] = param;
          return acc;
        },
        {} as Record<string, any>,
      );
      const requiredArgs: string[] = rule.requiredParams?.map((param) =>
        stringifyArg(parameters[param], requiredParamSpecsByName?.[param]),
      );
      const optionalArgs: string[] = rule.optionalParams?.map((paramSpec) =>
        stringifyArg(parameters[paramSpec.name], paramSpec),
      );

      args = [...(requiredArgs || []), ...(optionalArgs || [])];
    }

    return `${rule.label} ( ${args.join(', ')} )`;
  }

  return '';
};

const stringifyArg = (arg: any, paramSpec?: any): string => {
  if (Array.isArray(arg)) {
    if (paramSpec?.inputElement === 'object') {
      const keyValuePairs = [];

      for (let i = 0; i < arg.length; i += 2) {
        let key = stringifyArg(arg[i]);
        const value = stringifyArg(arg[i + 1]);

        // if there are no special chars in key, remove the surrounding quotes
        if (/^"[a-zA-Z_$][$\w\s]*"$/.test(key)) {
          key = key.slice(1, -1);
        }

        keyValuePairs.push(`${key} : ${value}`);
      }

      return `{ ${keyValuePairs.join(', ')} }`;
    }

    return `[ ${arg.map(stringifyArg).join(', ')} ]`;
  }

  if (arg.type === 'attributeRef' || arg.type === 'systemAttributeRef') {
    // escape parentheses
    const prefix = arg.type === 'attributeRef' ? '$' : '$$';
    return `${prefix}(${arg.name?.replace(/([()])/g, '\\$1') || ''})`;
  }

  if (arg.type === 'string') {
    // special handling of old transform rules from back when data_map field
    // used to have type "string" instead of "number"
    if (paramSpec?.inputElement === 'lookup') {
      return String(arg.value || '');
    }

    // escape double quotes
    return `"${arg.value?.replace(/"/g, '\\"') || ''}"`;
  }

  if (arg.type === 'number') {
    return arg.value;
  }

  return stringify(arg);
};

export default { stringify };
